// app/src/client/theme/themes.ts
//
// Single source of truth for the app's themes. Adding a new palette
// (Tokyo Night, etc.) is THREE steps and nothing else:
//   1. add one entry to THEMES below,
//   2. add one CSS block in client/Main.css (same `class` name),
//   3. add the label keys in client/i18n/translations.ts.
//
// Dark themes include "dark" in `classes` so that `dark:` Tailwind variants
// and the body-class dark detection (useIsDark / isDarkBackground) keep
// working unchanged, plus their own identity class for the palette.

export interface ThemeMeta {
  /** Stable id, persisted in localStorage under "color-theme". */
  id: string;
  /** i18n key path under t.themePicker for the display label. */
  labelKey: string;
  /** i18n key path under t.themePicker for the light/dark badge. */
  badgeKey: string;
  /** Body classes to apply for this theme (order matters; "dark" first). */
  classes: string[];
  /** Whether the surface is dark (drives chart "dark"/"light" modes, isDark). */
  isDark: boolean;
  /** Lucide icon name used by the theme picker. */
  icon: "Moon" | "Sun" | "Leaf" | "Sunrise" | "Building2";
  /** Two preview swatch colors [bg, accent] for the picker. */
  swatch: [string, string];
  /** When true, only admins can pick it. */
  adminOnly?: boolean;
}

export const THEMES: ThemeMeta[] = [
  {
    id: "dark",
    labelKey: "classicDark",
    badgeKey: "dark",
    classes: ["dark"],
    isDark: true,
    icon: "Moon",
    swatch: ["#080D1A", "#00FFD1"],
  },
  {
    id: "light",
    labelKey: "classicLight",
    badgeKey: "light",
    classes: [], // light = no class (the :root defaults)
    isDark: false,
    icon: "Sun",
    swatch: ["#F4F7FF", "#007A68"],
  },
  {
    id: "jungle",
    labelKey: "deepJungle",
    badgeKey: "dark",
    classes: ["dark", "theme-jungle"],
    isDark: true,
    icon: "Leaf",
    swatch: ["#040A06", "#10B981"],
    adminOnly: true, // "Coming Soon" pour les non-admins
  },
  {
    id: "sunrise",
    labelKey: "sunrise",
    badgeKey: "light",
    classes: ["theme-sunrise"],
    isDark: false,
    icon: "Sunrise",
    swatch: ["#FFF7ED", "#C05A00"],
    adminOnly: true, // "Coming Soon" pour les non-admins
  },
  // ===== ADD A NEW PALETTE HERE (+ one CSS block + i18n keys) =====
  {
    id: "tokyo-night",
    labelKey: "tokyoNight",
    badgeKey: "dark",
    classes: ["dark", "theme-tokyo"],
    isDark: true,
    icon: "Building2",
    swatch: ["#1A1B26", "#7AA2F7"],
  },
];

/** All body classes any theme can apply — used to clear before applying one. */
export const ALL_THEME_CLASSES: string[] = Array.from(
  new Set(THEMES.flatMap((t) => t.classes)),
);

/**
 * Resolve the active theme id from the classes currently on <body>.
 * Identity classes (theme-jungle, theme-tokyo, theme-sunrise) are unique per
 * palette; a bare "dark"/no-class falls back to classic dark/light. Used by
 * useThemeId() so charts can re-read their CSS-variable colors on ANY theme
 * switch — not only dark↔light (isDark misses dark→jungle, light→sunrise…).
 */
export function resolveThemeIdFromClasses(classList: DOMTokenList): string {
  // Prefer a theme whose identity class (the non-"dark" one) is present.
  const byIdentity = THEMES.find((theme) =>
    theme.classes.some((cls) => cls !== "dark" && classList.contains(cls)),
  );
  if (byIdentity) return byIdentity.id;
  return classList.contains("dark") ? "dark" : "light";
}

export function getTheme(id: string): ThemeMeta {
  return THEMES.find((t) => t.id === id) ?? THEMES[0];
}
